import moment from 'moment-timezone';

import { DataInterval, ReportsCategoryAxisOptions } from '@core/reports-types';
import type { TimeSpan } from '@core/reports-util';
import { resolveWeekType } from '@core/time-legacy';

import { formatSingleTickAxisLabel } from './date-format';

const mapArgsFromCaller = (handler: 'formatter' | 'interval', args: number[]) => {
  switch (handler) {
    case 'interval':
      return [args[1], args[0]];

    case 'formatter':
    default:
      return [args[0], args[1]];
  }
};

const xAxisTicker = <T>(
  startDate: moment.Moment,
  endDate: moment.Moment,
  caller: 'formatter' | 'interval',
  fn: (tick: moment.Moment, previousTick: moment.Moment, isFirst: boolean) => T,
) => {
  let previousTick: moment.Moment;
  return (...args: number[]) => {
    const [value, index] = mapArgsFromCaller(caller, args);

    const tick = moment(value);

    const label = fn(tick, previousTick, index === 0);

    previousTick = tick;
    return label;
  };
};

const formatter = (tick: moment.Moment, prevTick: moment.Moment | undefined, isFirst: boolean) => {
  const weekType = resolveWeekType();
  const tickEndOfWeek = tick.clone().endOf(weekType);
  const tickEndOfWeekMonth = tickEndOfWeek.month();
  const prevTickEndOfWeek = prevTick?.clone().endOf(weekType);
  const prevTickEndOfWeekMonth = prevTickEndOfWeek?.month();

  if (isFirst) {
    return tickEndOfWeek.format('MMM');
  }

  if (tickEndOfWeekMonth !== prevTickEndOfWeekMonth) {
    const month = tickEndOfWeek.format('MMM');
    if (tickEndOfWeek.year() !== prevTickEndOfWeek?.year()) {
      return `{level1|${tickEndOfWeek.year()}\n${month}}`;
    }

    return month;
  }

  return '';
};

const interval = (tick: moment.Moment, prevTick: moment.Moment | undefined, isFirst: boolean) => {
  if (isFirst) {
    return true;
  }
  const weekType = resolveWeekType();
  const tickEndOfWeekMonth = tick.clone().endOf(weekType).month();
  const prevTickEndOfWeekMonth = prevTick?.clone().endOf(weekType).month();

  return tickEndOfWeekMonth !== prevTickEndOfWeekMonth;
};

export const weekly = (ticksCount: number, ts: TimeSpan): { xAxis: ReportsCategoryAxisOptions } => {
  const startDate = moment(ts.start);
  const endDate = moment(ts.end);

  const singleTickAxis = ticksCount === 1;

  const xAxisFormatter = xAxisTicker<string>(startDate, endDate, 'formatter', formatter);
  const xAxisTicksInterval = xAxisTicker<boolean>(startDate, endDate, 'interval', interval);

  return {
    xAxis: {
      type: 'category',
      axisTick: {
        interval: xAxisTicksInterval,
        alignWithLabel: true,
      },
      axisLabel: {
        fontSize: 12,
        rich: {
          level1: {
            fontWeight: 'bold',
          },
        },
        formatter: singleTickAxis ? formatSingleTickAxisLabel(DataInterval.Weekly) : xAxisFormatter,
      },
    },
  };
};
